// components/expenses/DeleteExpenseDialog.jsx
import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Typography,
    Box,
    Chip,
    CircularProgress
} from '@mui/material';
import { Warning as WarningIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const DeleteExpenseDialog = ({ open, onClose, expense, onConfirm }) => {
    const { t } = useTranslation();
    const [reason, setReason] = useState('');
    const [loading, setLoading] = useState(false);

    const handleConfirm = async () => {
        setLoading(true);
        try {
            await onConfirm(reason.trim() || null);
        } finally {
            setLoading(false);
            setReason('');
        }
    };

    const handleClose = () => {
        setReason('');
        onClose();
    };

    if (!expense) return null;

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <WarningIcon color="error" />
                {t('expenses.deleteConfirmTitle', 'Delete Expense')}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body1" sx={{ mb: 2 }}>
                    {t('expenses.deleteConfirmMessage', 'Are you sure you want to delete this expense? It will be moved to the trash and can be restored later.')}
                </Typography>

                {/* Expense Summary */}
                <Box
                    sx={{
                        p: 2,
                        mb: 3,
                        borderRadius: 1,
                        backgroundColor: 'rgba(244, 67, 54, 0.04)',
                        border: '1px solid',
                        borderColor: 'error.light',
                    }}
                >
                    <Typography variant="subtitle2" fontWeight="bold">
                        {expense.name}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                        <Chip
                            label={`${expense.amount?.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                            })}`}
                            size="small"
                            color="error"
                            variant="outlined"
                        />
                        {expense.category?.name && (
                            <Chip
                                label={expense.category.name}
                                size="small"
                                variant="outlined"
                            />
                        )}
                        {expense.branch?.name && (
                            <Chip
                                label={expense.branch.name}
                                size="small"
                                variant="outlined"
                            />
                        )}
                    </Box>
                </Box>

                {/* Reason Field */}
                <TextField
                    fullWidth
                    label={t('expenses.deletionReason', 'Reason for deletion')}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    multiline
                    rows={3}
                    placeholder={t('expenses.deletionReasonPlaceholder', 'Optional: Provide a reason for deleting this expense...')}
                    helperText={t('expenses.deletionReasonHelp', 'This reason will be stored and visible in the deleted expenses list.')}
                />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
                <Button onClick={handleClose} disabled={loading}>
                    {t('common.cancel', 'Cancel')}
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleConfirm}
                    disabled={loading}
                    startIcon={loading ? <CircularProgress size={16} /> : null}
                >
                    {loading
                        ? t('common.deleting', 'Deleting...')
                        : t('common.delete', 'Delete')
                    }
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default DeleteExpenseDialog;